import {Alert, Autocomplete, Box, Chip, TextField} from '@mui/material';
import {ChangeEvent, FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {businessesApi, useCloneRoleMutation, useCreateRoleMutation, useUpdateRoleMutation} from 'src/api';
import {AMPInput} from 'src/components/common/AMPInput';
import AMPModal from 'src/components/common/AMPModal';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {actionsWrapperSx, formSx} from 'src/components/forms/AddReManager/AddReManager.sx';
import {useScope} from 'src/permissions/hooks';
import {BusinessDTO, RoleDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';

interface RoleFormModalProps {
    /** Absent: create from scratch. Present with `clone`: copy it. Present without: rename it. */
    role?: RoleDTO;
    clone?: boolean;
    onClose: () => void;
}

/**
 * What a holder of this role will see: their own record, their company, their properties, or the whole
 * business. GLOBAL is missing on purpose — it spans every tenant on the installation and is not
 * something a business grants itself.
 *
 * Not to be confused with the role's reach, edited below: this is how much data a holder sees inside
 * a business, that is which businesses the role can be used in at all.
 */

/**
 * Create, rename or copy a role.
 *
 * Cloning is its own mode rather than a bare button, because "clone" on its own does not say why
 * anyone would want it. The explanation belongs where the decision is made: a role reaching more
 * businesses than yours cannot be edited in place — you take a copy scoped to yours and change that.
 *
 * Built from AMPInput and AMPFormSelect rather than raw MUI fields: a global theme override paints
 * every `helperText` in `error.main`, so plain fields render their hints as if they were validation
 * failures. The `description` prop is the grey one.
 */
export const RoleFormModal: FC<RoleFormModalProps> = ({role, clone, onClose}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const scope = useScope();

    const [createRole, {isLoading: creating}] = useCreateRoleMutation();
    const [updateRole, {isLoading: updating}] = useUpdateRoleMutation();
    const [cloneRole, {isLoading: cloning}] = useCloneRoleMutation();

    const renaming = !!role && !clone;
    const [name, setName] = useState(
        clone
            ? intl.formatMessage(
                {id: 'accessControlCopyOf', defaultMessage: '{name} (copy)'},
                {name: role?.name ?? ''}
            )
            : role?.name ?? ''
    );
    const [description, setDescription] = useState(role?.description ?? '');

    // Reach is the platform's to set: a business widening its own role to a second tenant would be
    // handing itself edit rights over that tenant's access. The server refuses it from anyone else,
    // so for everyone else the control is simply absent rather than present and disabled.
    const platformWide = scope?.type === 'GLOBAL';
    const {data: businessPage} = businessesApi.useGetBusinessesQuery(
        {params: {page: 0, size: 500}},
        {skip: !platformWide || !renaming}
    );
    const allBusinesses = businessPage?.content ?? [];
    const [reach, setReach] = useState<{id: number; name?: string}[]>(role?.businesses ?? []);
    // No reach here on purpose. A role says what may be done; how far it reaches follows from the
    // kind of account holding it, so a role cannot answer for it - it does not know who it will be
    // given to. Asking anyway produced a field whose answer was overridden in every case but one.

    const busy = creating || updating || cloning;
    const invalid = !name.trim();

    const title = renaming
        ? intl.formatMessage({id: 'accessControlEditRole', defaultMessage: 'Edit role'})
        : clone
            ? intl.formatMessage({id: 'accessControlCloneRole', defaultMessage: 'Copy role'})
            : intl.formatMessage({id: 'accessControlCreateRole', defaultMessage: 'New role'});

    // The id arrives inside the response envelope, as `payload`. This used to read `.id` off the
    // envelope, which is undefined, so creating or copying a role never actually opened it.
    const openRole = (id?: number, replace = false) => {
        if (id) {
            navigate(
                AppRouteSpace([
                    AppRouteEnum.app,
                    AppRouteEnum.accessControl,
                    AppRouteEnum.roles,
                    String(id),
                ]),
                {replace}
            );
        }
    };

    const submit = async () => {
        try {
            if (renaming && role) {
                const {payload: changed} = await updateRole({
                    roleId: role.id,
                    data: {
                        name,
                        description,
                        // Omitted entirely when the caller cannot set it: sending the field at all
                        // would have the server refuse the whole rename.
                        ...(platformWide ? {businessIds: reach.map((business) => business.id)} : {}),
                    },
                }).unwrap();
                // Renaming a shared starting role renames this business's own copy; follow it there.
                if (changed && changed !== role.id) {
                    openRole(changed, true);
                }
            } else if (clone && role) {
                openRole((await cloneRole({roleId: role.id, data: {name}}).unwrap()).payload);
            } else {
                // Created empty on purpose: granting happens in the role's own editor, where each
                // permission carries its description. A role arriving pre-filled from a modal would be
                // handing out access nobody had read.
                openRole(
                    (await createRole({
                        name,
                        description,
                        permissionNames: [],
                    }).unwrap()).payload
                );
            }
            NotifySuccess(intl.formatMessage({id: 'saved', defaultMessage: 'Saved'}));
            onClose();
        } catch (error) {
            NotifyError(
                isErrorObject(error)
                    ? error.data.message
                    : intl.formatMessage({id: 'somethingWentWrong', defaultMessage: 'Something went wrong'})
            );
        }
    };

    return (
        <AMPModal open handleClose={onClose} title={title} type="md" fullWidth withoutPaddings>
            <Box sx={formSx(theme)}>
                {clone && (
                    <Alert severity="info" sx={{borderRadius: '14px'}}>
                        {intl.formatMessage({
                            id: 'accessControlCloneExplained',
                            defaultMessage:
                                'Platform roles are shared by every business, so they cannot be edited directly. This makes a copy that belongs to you, starting with the same permissions, which you can then change freely.',
                        })}
                    </Alert>
                )}

                <AMPInput
                    input="name"
                    type="text"
                    label="name"
                    value={name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                />

                <AMPInput
                    input="description"
                    type="text"
                    label="description"
                    value={description}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
                />

                {renaming && platformWide && (
                    <Autocomplete
                        multiple
                        options={allBusinesses}
                        value={allBusinesses.filter((business) =>
                            reach.some((chosen) => chosen.id === business.id)
                        )}
                        onChange={(_event, value: BusinessDTO[]) =>
                            setReach(value.map((business) => ({id: business.id, name: business.name})))
                        }
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderTags={(value, getTagProps) =>
                            value.map((business, index) => (
                                <Chip
                                    size="small"
                                    label={business.name}
                                    {...getTagProps({index})}
                                    key={business.id}
                                />
                            ))
                        }
                        renderInput={(inputParams) => (
                            <TextField
                                {...inputParams}
                                label={intl.formatMessage({
                                    id: 'accessControlRoleReach',
                                    defaultMessage: 'Works in',
                                })}
                            />
                        )}
                    />
                )}
            </Box>

            <Box sx={actionsWrapperSx(theme)}>
                <CancelButton text="cancel" handler={onClose}/>
                <SubmitButton
                    text="save"
                    color="brand"
                    isSubmitting={busy || invalid}
                    handler={submit}
                />
            </Box>
        </AMPModal>
    );
};
